import * as React from 'react';
import * as autoBind from 'react-autobind';
import * as classNames from 'classnames';
import {ControlButton, TscButtonType} from '@techsmith/tsc-cloud-style-guide';
import Constants, {AnnotationModes, MobileViewingModes} from '../../globals/Constants';
import AvatarWrapper from '../AvatarWrapper/AvatarWrapper';
import AnnotationToolbarContainer from '../AnnotationToolbar/AnnotationToolbarContainer';
import UserModel from '../../types/UserModel';
import IVideoPlayer from '../../types/IVideoPlayer';
import TextAreaWrapper from '../TextAreaWrapper/TextAreaWrapper';
import Strings from '../../globals/Strings';
import LocalStorageWrapper from '../../services/LocalStorageWrapper/LocalStorageWrapper';
import Themeable from '../../types/Themeable';
import SelfIdentifiedCommentingAs from '../SelfIdentifiedCommentingAs/SelfIdentifiedCommentingAs';
import ReviewModel from '../../types/ReviewModel';
import ReviewVersionModel from '../../types/ReviewVersionModel';
import ContentTypeParser from '../../utils/ContentTypeParser/ContentTypeParser';
import GoogleAnalyticsService from '../../services/GoogleAnalyticsService/GoogleAnalyticsService';
import AddCommentIconContainer from '../AddCommentIcon/AddCommentIconContainer';
import AriaLiveStatusHelper from '../../utils/AriaLiveStatusHelper';
import {RouteComponentProps} from 'react-router';

export default class Feedback extends React.Component<FeedbackProps, FeedbackState> {
   readonly state: FeedbackState = {
      input: LocalStorageWrapper.getItem(this.props.localStorageKey) || '',
      isSubmitting: false
   };

   constructor(props: FeedbackProps) {
      super(props);
      autoBind(this);
   }

   componentWillUnmount() {
      const {setAnnotationMode, setMobileViewingMode} = this.props;
      setAnnotationMode(AnnotationModes.display);
      setMobileViewingMode(MobileViewingModes.conversation);
   }

   handleChange(text: string): void {
      const {localStorageKey} = this.props;
      this.setState({input: text});

      if (localStorageKey) {
         LocalStorageWrapper.setItem(localStorageKey, text);
      }
   }

   resetInput(): void {
      const {localStorageKey} = this.props;

      this.setState({input: ''});
      if (localStorageKey) {
         LocalStorageWrapper.removeItem(localStorageKey);
      }
   }

   getGaAction(alternatePlaceholderText: string, input: string): string {
      if (alternatePlaceholderText) {
         return Constants.googleAnalytics.commenting.actions.createReply;
      }
      if (input) {
         return Constants.googleAnalytics.commenting.actions.createComment;
      }
      return Constants.googleAnalytics.commenting.actions.createEmptyComment;
   }

   submit(): Promise<void> {
      const {currentUser, review, alternatePlaceholderText, canvas, annotationExists, setMobileViewingMode, setAnnotationMode} = this.props;
      const {input, isSubmitting} = this.state;
      const trimmedInput = input.trim();
      const readyToSubmit = ((trimmedInput && trimmedInput.length <= Constants.comment.maxLength) || annotationExists);

      if (readyToSubmit && !isSubmitting) {
         this.setState({isSubmitting: true});
         setMobileViewingMode(MobileViewingModes.conversation);
         setAnnotationMode(AnnotationModes.none);
         canvas.off('mouse:down');
         canvas.off('mouse:move');
         canvas.off('mouse:up');

         const gaAction = this.getGaAction(alternatePlaceholderText, input);
         const isOwner = currentUser && currentUser.userId && review && review.Reviewers.filter(reviewer => reviewer.Id === currentUser.userId && reviewer.Role === Constants.reviewerRoles.owner).length > 0;
         const gaLabel = isOwner ? Constants.googleAnalytics.commenting.labels.reviewOwner : Constants.googleAnalytics.commenting.labels.nonReviewOwner;
         GoogleAnalyticsService.trackCommenting(currentUser.userId, gaAction, gaLabel);

         return this.props.addComment(trimmedInput).then(() => {
            this.resetInput();
            AriaLiveStatusHelper.readAlert(Strings.accessibility.comments.commentSubmittedAriaLiveText);
            this.setState({isSubmitting: false});
         }).catch(() => {
            AriaLiveStatusHelper.readAlert(Strings.errors.failedSaveComment);
            this.setState({isSubmitting: false});
         });
      }
   }

   pausePlayer(): void {
      const {player, reviewVersion} = this.props;

      if (player && ContentTypeParser.getContentTypeIsVideo(reviewVersion.MediaContentType)) {
         player.pause();
      }
   }

   cancelOnClick() {
      const {setMobileViewingMode, setAnnotationMode} = this.props;
      setMobileViewingMode(MobileViewingModes.conversation);
      setAnnotationMode(AnnotationModes.none);
      this.resetInput();
   }

   render() {
      const {input, isSubmitting} = this.state;
      const {showAnnotationsBar, alternatePlaceholderText, currentUser, canvas, hasFocus, onBlur, maxRows, themeName, reviewFeedbackFocused, isUserSelfIdentified, review, reviewVersion, goToLatest, annotationExists, mobileViewingMode} = this.props;
      const isViewOnly = review.ReviewVersions.filter(ver => !ver.IsDeleted).pop().VersionNumber !== reviewVersion.VersionNumber;
      const textLimitDifference = input.length - Constants.comment.maxLength;
      const textLimitMessage = textLimitDifference === 1 ? ' character past limit' : ' characters past limit';
      const commentInputClasses = classNames('comment-input', {'past-limit': textLimitDifference > 0});
      const placeHolderText = alternatePlaceholderText ? alternatePlaceholderText : Strings.labels.addComment;
      const canWriteComment = !isSubmitting && currentUser && currentUser.userId && canvas;
      const canSubmitComment = canWriteComment && (input.trim().length > 0 || annotationExists) && textLimitDifference <= 0;
      const feedbackClassNames = (!showAnnotationsBar || mobileViewingMode === MobileViewingModes.feedback) ? 'feedback' : 'feedback hide-on-mobile';
      const feedbackStyle = showAnnotationsBar ? {} : {boxShadow: 'none'};

      const cancelCommentButton = <ControlButton
         themeName={themeName}
         buttonType={TscButtonType.secondary}
         label="Cancel"
         onClick={this.cancelOnClick}
         testId="feedback-cancel-button"
      />;

      return (
         <React.Fragment>
            <div className={feedbackClassNames} style={feedbackStyle}>
               {!isViewOnly && <div className="feedback-inline">
                  <div className="user-input" onFocus={this.pausePlayer}>
                     <div className="avatar-and-comment">
                        {currentUser && currentUser.userId && <AvatarWrapper user={currentUser} />}
                        <TextAreaWrapper
                           id="feedback-comment-input"
                           maxRows={maxRows}
                           autoFocus={false}
                           text={input}
                           placeholder={placeHolderText}
                           className={commentInputClasses}
                           onChange={this.handleChange}
                           onEnter={this.submit}
                           onEscape={this.resetInput}
                           hasFocus={hasFocus}
                           onBlur={onBlur}
                           onFocus={reviewFeedbackFocused}
                           readOnly={!canWriteComment} />
                     </div>
                     {textLimitDifference > 0 && <div className="text-limit">{textLimitDifference + textLimitMessage}</div>}
                  </div>
                  <div className="feedback-footer">
                     {isUserSelfIdentified && <SelfIdentifiedCommentingAs isInline={true} userName={currentUser.name} />}
                     <div className="user-actions">
                        <ControlButton
                           disabled={!canSubmitComment}
                           themeName={themeName}
                           buttonType={TscButtonType.primary}
                           label={Strings.labels.submit}
                           onClick={this.submit}
                           testId="feedback-submit-button"
                        />
                        <div className="mobile-cancel-comment">
                           {cancelCommentButton}
                        </div>
                        <div className="annotation-controls">
                           {canvas && showAnnotationsBar && <AnnotationToolbarContainer pausePlayer={this.pausePlayer} annotationExists={annotationExists} />}
                        </div>
                     </div>
                  </div>
               </div>}
               {isViewOnly && showAnnotationsBar && <div className="view-only-container">
                  <div className="view-only-header-text">{Strings.labels.versions.viewOnlyHeader}</div>
                  <div className="view-only-body-text">
                     {Strings.labels.versions.viewOnlyBodyText1}
                     <a onClick={goToLatest} className="view-only-body-text">{Strings.labels.versions.viewOnlyBodyLinkText}</a>
                     {Strings.labels.versions.viewOnlyBodyText2}
                  </div>
               </div>
               }
            </div>
            {(showAnnotationsBar && !isViewOnly && canvas) && <AddCommentIconContainer pausePlayer={this.pausePlayer} />}
         </React.Fragment>
      );
   }
}

export interface FeedbackProps extends RouteComponentProps, Themeable {
   isUserSelfIdentified?: boolean;
   currentUser: UserModel;
   annotationMode: AnnotationModes;
   player: IVideoPlayer;
   canvas: any;
   alternatePlaceholderText: string;
   hasFocus?: boolean;
   localStorageKey?: string;
   showAnnotationsBar: boolean;
   maxRows?: number;
   review: ReviewModel;
   reviewVersion: ReviewVersionModel;
   annotationExists: boolean;
   mobileViewingMode: MobileViewingModes;
   setMobileViewingMode(mobileViewingMode: MobileViewingModes): void;
   addComment(text: string): Promise<any>;
   setAnnotationMode(annotationMode: AnnotationModes): void;
   reviewFeedbackFocused(): void;
   onBlur?(): void;
   goToLatest?(): void;
}

export interface FeedbackState {
   input: string;
   isSubmitting: boolean;
}
